import React from 'react';
import { Col, Form, Row, Select } from 'antd';
import type { FormInstance } from 'antd';
import { buscarEpc } from '../../api/oportunidadesApi';
import type { Catalogos } from '../../api/oportunidadesApi';
import { SelectorRemoto } from './SelectorRemoto';
import type { OpcionRemota } from './SelectorRemoto';
import { detallesDeOrigen, faltasOrigen, limpiarAlCambiarOrigen } from './reglas';

interface CamposOrigenProps {
  form: FormInstance;
  catalogos: Catalogos;
  /** Etiquetas de las EPC ya guardadas (edición). */
  epcIniciales?: { entidad?: OpcionRemota; persona?: OpcionRemota };
  /** Campo que va en la línea de la iniciativa (en venta, la forma de contacto). */
  junto?: React.ReactNode;
}

const buscarEpcComo = (persona: boolean) => async (texto: string): Promise<OpcionRemota[]> =>
  (await buscarEpc(persona, texto || undefined)).map((e) => ({ value: e.id, label: e.nombre ?? `#${e.id}` }));

/**
 * Iniciativa, origen y detalle en cascada (R-09), iguales en venta, compra y solicitud. Con origen
 * EPCs el detalle desaparece y aparecen «EPC entidad» y «EPC persona»: al menos una. Al cambiar de
 * origen se vacía el detalle y, si deja de ser EPCs, también los campos EPC.
 * Disposición (spec rev. 3): una línea con la iniciativa (y `junto`) y otra con el origen y su
 * detalle, o el origen y los dos campos EPC.
 */
export const CamposOrigen: React.FC<CamposOrigenProps> = ({ form, catalogos, epcIniciales, junto }) => {
  const idOrigen: number | undefined = Form.useWatch('idOrigen', form);
  const esEpc = idOrigen !== undefined && idOrigen === catalogos.idOrigenEpc;
  const detalles = detallesDeOrigen(catalogos.detalles, idOrigen);

  const alCambiarOrigen = (nuevo?: number) => {
    const actual = form.getFieldsValue(['idIniciativa', 'idDetalle', 'idEpcEntidad', 'idEpcPersona']);
    form.setFieldsValue(limpiarAlCambiarOrigen(actual, nuevo, catalogos.idOrigenEpc));
  };

  const alMenosUnaEpc = {
    validator: async () => {
      if (!esEpc) return;
      const falta = faltasOrigen(form.getFieldsValue(['idIniciativa', 'idOrigen', 'idEpcEntidad', 'idEpcPersona']), catalogos.idOrigenEpc)
        .find((x) => x.includes('EPC'));
      if (falta) throw new Error(falta);
    },
  };

  return (
    <>
      <Row gutter={12}>
        <Col xs={24} md={12}>
          <Form.Item name="idIniciativa" label="Iniciativa" rules={[{ required: true, message: 'La iniciativa es obligatoria.' }]}>
            <Select
              placeholder="Elige"
              options={catalogos.iniciativas.map((i) => ({ value: i.id, label: i.descripcion }))}
            />
          </Form.Item>
        </Col>
        {junto && <Col xs={24} md={12}>{junto}</Col>}
      </Row>
      <Row gutter={12}>
      <Col xs={24} md={esEpc ? 8 : 12}>
        <Form.Item name="idOrigen" label="Origen" rules={[{ required: true, message: 'El origen es obligatorio.' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Elige"
            onChange={alCambiarOrigen}
            options={catalogos.origenes.map((o) => ({ value: o.id, label: o.descripcion }))}
          />
        </Form.Item>
      </Col>
      {!esEpc && (
        <Col xs={24} md={12}>
          <Form.Item name="idDetalle" label="Detalle" rules={[{ required: true, message: 'El detalle es obligatorio.' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled={idOrigen === undefined}
              placeholder={idOrigen === undefined ? 'Elige antes el origen' : 'Elige'}
              options={detalles.map((d) => ({ value: d.id, label: d.descripcion }))}
            />
          </Form.Item>
        </Col>
      )}
      {esEpc && (
        <>
          <Col xs={24} md={8}>
            <Form.Item name="idEpcEntidad" label="EPC entidad" dependencies={['idEpcPersona']} rules={[alMenosUnaEpc]}>
              <SelectorRemoto
                buscar={buscarEpcComo(false)}
                cargarAlAbrir
                minimo={1}
                placeholder="Entidad EPC"
                opcionesIniciales={epcIniciales?.entidad ? [epcIniciales.entidad] : []}
                aria-label="EPC entidad"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="idEpcPersona" label="EPC persona" dependencies={['idEpcEntidad']} rules={[alMenosUnaEpc]}>
              <SelectorRemoto
                buscar={buscarEpcComo(true)}
                cargarAlAbrir
                minimo={1}
                placeholder="Persona EPC"
                opcionesIniciales={epcIniciales?.persona ? [epcIniciales.persona] : []}
                aria-label="EPC persona"
              />
            </Form.Item>
          </Col>
        </>
      )}
      </Row>
    </>
  );
};
